import type {
  DataObjectType,
  DataObjectAppJSON,
  DataObjectFieldsSchema,
  DataObjectField,
  DataObjectFieldsSchemaAppJSON,
  DataObjectFieldAppJSON
} from "@certa/types/src/dataObjects.types";
import {
  forwardParseDataObject,
  forwardParseXRelationship,
  forwardParseXRollup
} from "./forwardParseDataObject";

/**
 * Forward parse a single DataObjectField to DataObjectFieldAppJSON
 * Converts __isFrozen to __studio_frozen with lossless transformation
 * Note: DataObjectFieldAppJSON keeps camelCase properties from DataObjectField
 */
const forwardParseDataObjectFieldAppJSON = (
  field: DataObjectField
): DataObjectFieldAppJSON => {
  const {
    isIndexed,
    hasUniqueItems,
    "x-rollup": xRollup,
    "x-relationship": xRelationship,
    __isFrozen: isFrozen,
    ...restFieldProps
  } = field;

  // Create the result with proper type - fields remain in camelCase
  const result: DataObjectFieldAppJSON = {
    ...restFieldProps
  };

  if (isFrozen !== undefined) {
    result.__studio_frozen = isFrozen;
  }

  if (isIndexed !== undefined) {
    result.indexed = isIndexed;
  }

  if (hasUniqueItems !== undefined) {
    result.uniqueItems = hasUniqueItems;
  }

  if (xRollup) {
    result["x-rollup"] = forwardParseXRollup(xRollup);
  }

  if (xRelationship) {
    result["x-relationship"] = forwardParseXRelationship(xRelationship);
  }

  return result;
};

/**
 * Forward parse DataObjectFieldsSchema to DataObjectFieldsSchemaAppJSON
 * Note: Schema structure remains the same, only field properties change (__isFrozen -> __studio_frozen)
 */
const forwardParseDataObjectSchemaAppJSON = (
  schema: DataObjectFieldsSchema
): DataObjectFieldsSchemaAppJSON => {
  const properties: DataObjectFieldsSchemaAppJSON["properties"] = {};

  for (const [fieldTag, field] of Object.entries(schema.properties)) {
    properties[fieldTag] = forwardParseDataObjectFieldAppJSON(field);
  }

  return {
    properties,
    required: schema.required
  };
};

/**
 * Forward parse DataObjectType to DataObjectAppJSON
 * Converts known properties from camelCase to snake_case while preserving unknown properties
 * for lossless transformation. Also handles studio-specific fields.
 */
export const forwardParseDataObjectAppJSON = (
  dataObject: DataObjectType
): DataObjectAppJSON => {
  const {
    tag,
    schema,
    uiSchema,
    translatedUiSchema,
    filterConfig,
    __saveId,
    __description,
    ...unknownProps // Preserve any unknown properties
  } = dataObject;

  // First, parse the DataObject to Raw format (snake_case)
  const rawDataObject = forwardParseDataObject(dataObject);

  // Now convert schema fields to AppJSON format
  const schemaAppJSON = forwardParseDataObjectSchemaAppJSON(schema);

  const result: DataObjectAppJSON = {
    ...rawDataObject,
    schema: schemaAppJSON,
    ...unknownProps
  };

  if (__saveId !== undefined) {
    result.__studio_save_id = __saveId;
  }
  if (__description !== undefined) {
    result.__studio_description = __description;
  }

  return result;
};
